import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "@/components/StatusBadge";
import TrustLine from "@/components/TrustLine";
import {
  Target, Plus, Play, Pause, Trash2, Clock, Settings, RefreshCw, PhoneOff, Mail, Brain
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [campaignLeadCount, setCampaignLeadCount] = useState(null);
  const [importingLeads, setImportingLeads] = useState(false);
  const [showFollowUpSettings, setShowFollowUpSettings] = useState(null); // campaign for settings modal
  const [followUpSettings, setFollowUpSettings] = useState({
    enabled: true,
    no_answer_retry_enabled: true,
    no_answer_retry_count: 3,
    no_answer_retry_delay_hours: 24,
    voicemail_followup_enabled: true,
    voicemail_followup_delay_hours: 48
  });
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    ai_script: "Hello, this is an AI assistant calling about credit card processing solutions for your business. Am I speaking with the owner or manager?",
    calls_per_day: 100,
    calls_per_hour: 0,
    calling_hours_start: "09:00",
    calling_hours_end: "17:00",
    calling_days: ["mon","tue","wed","thu","fri"],
    voicemail_enabled: true,
    voicemail_message: "",
    response_wait_seconds: 4,
    company_name: "",
    icp_config: {
      target_industries: [],
      preferred_company_sizes: ["11-50", "51-200"],
      min_intent_signals: 1,
      preferred_roles: ["Owner", "CEO", "Manager", "Director"]
    },
    min_icp_score: 0,
    // A/B Testing
    ab_testing_enabled: false,
    script_variant_b: "",
    ab_split_percentage: 50
  });

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data || []);
    } catch (error) {
      console.error("Failed to load agents", error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchAgents();
  }, []);

  const importCampaignCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editing?.id) return;

    const formData = new FormData();
    formData.append("file", file);

    setImportingLeads(true);
    try {
      const response = await axios.post(
        `${API}/campaigns/${editing.id}/import-leads`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setCampaignLeadCount(response.data.campaign_leads ?? null);

      toast.success(
        `Imported ${response.data.inserted || 0} leads` +
        (response.data.updated ? `, updated ${response.data.updated}` : "")
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        "Failed to import campaign leads"
      );
    } finally {
      setImportingLeads(false);
      event.target.value = "";
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name || !newCampaign.ai_script) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/campaigns`, newCampaign);
      toast.success("Campaign created!");
      setShowCreate(false);
      setNewCampaign({ name: "", description: "", ai_script: "", calls_per_day: 100, calls_per_hour: 0, calling_hours_start: "09:00", calling_hours_end: "17:00", calling_days: ["mon","tue","wed","thu","fri"], voicemail_enabled: true, voicemail_message: "", response_wait_seconds: 4, company_name: "", icp_config: { target_industries: [], preferred_company_sizes: ["11-50", "51-200"], min_intent_signals: 1, preferred_roles: ["Owner", "CEO", "Manager", "Director"] }, min_icp_score: 0 });
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to create campaign");
    }
  };

  const openEditCampaign = async (campaign) => {
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaign.id}?_=${Date.now()}`,
        { headers: { "Cache-Control": "no-cache" } }
      );
      const fresh = response.data;

      const vmStatus = await axios.get(
        `${API}/campaigns/${campaign.id}/voicemail-status?_=${Date.now()}`
      );

      fresh.voicemail_audio_url = vmStatus.data.voicemail_audio_url || "";
      fresh.agent_id = vmStatus.data.agent_id || fresh.agent_id || "";

      try {
        const leadCount = await axios.get(
          `${API}/campaigns/${campaign.id}/leads/count?_=${Date.now()}`
        );
        setCampaignLeadCount(leadCount.data.count ?? 0);
      } catch (countError) {
        console.error("Failed to load campaign lead count", countError);
        setCampaignLeadCount(null);
      }

      setEditing(fresh);
      setEditDraft({
        calls_per_day: fresh.calls_per_day ?? 100,
        calls_per_hour: fresh.calls_per_hour ?? 0,
        calling_hours_start: fresh.calling_hours_start ?? "09:00",
        calling_hours_end: fresh.calling_hours_end ?? "17:00",
        calling_days: fresh.calling_days ?? ["mon","tue","wed","thu","fri"],
        company_name: fresh.company_name ?? "",
        callback_number: fresh.callback_number ?? "",
        voicemail_enabled: fresh.voicemail_enabled ?? true,
        voicemail_message: fresh.voicemail_message ?? "",
        agent_id: fresh.agent_id ?? "",
        voicemail_audio_url: fresh.voicemail_audio_url ?? ""
      });
    } catch (error) {
      toast.error("Failed to load campaign settings");
    }
  };

  const saveEditCampaign = async () => {
    if (!editing || !editDraft) return;

    if (editDraft.voicemail_enabled && !editDraft.callback_number) {
      toast.error("Add a callback number before enabling voicemail drops.");
      return;
    }

    try {
      const response = await axios.put(`${API}/campaigns/${editing.id}`, {
        calls_per_day: editDraft.calls_per_day,
        calls_per_hour: editDraft.calls_per_hour,
        calling_hours_start: editDraft.calling_hours_start,
        calling_hours_end: editDraft.calling_hours_end,
        calling_days: editDraft.calling_days,
        company_name: editDraft.company_name,
        callback_number: editDraft.callback_number,
        voicemail_enabled: editDraft.voicemail_enabled,
        voicemail_message: editDraft.voicemail_message,
        agent_id: editDraft.agent_id
      });

      const updated = response.data;
      setEditing(updated);
      setEditDraft({
        ...editDraft,
        agent_id: updated.agent_id ?? editDraft.agent_id,
        callback_number: updated.callback_number ?? editDraft.callback_number,
        voicemail_audio_url: updated.voicemail_audio_url ?? ""
      });
      toast.success(updated.voicemail_audio_url ? "Campaign updated — voicemail ready" : "Campaign updated");
      fetchCampaigns();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update campaign");
    }
  };

  const toggleCampaign = async (campaign) => {
    const endpoint = campaign.status === 'active' ? 'pause' : 'start';
    const linkedAgent = agents.find(a => a.id === campaign.agent_id);
    if (
      endpoint === 'start' &&
      campaign.voicemail_enabled &&
      linkedAgent?.voice_type === 'cloned' &&
      !campaign.voicemail_audio_url
    ) {
      toast.error("Cloned voicemail is not ready. Edit the campaign, save it, and preview the voicemail first.");
      return;
    }
    try {
      await axios.post(`${API}/campaigns/${campaign.id}/${endpoint}`);
      toast.success(`Campaign ${endpoint === 'start' ? 'started' : 'paused'}`);
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to update campaign");
    }
  };

  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const openFollowUpSettings = async (campaign) => {
    setShowFollowUpSettings(campaign);
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/followup-settings`);
      setFollowUpSettings(response.data.settings);
    } catch (error) {
      // Use defaults if no settings exist
      setFollowUpSettings({
        enabled: true,
        no_answer_retry_enabled: true,
        no_answer_retry_count: 3,
        no_answer_retry_delay_hours: 24,
        voicemail_followup_enabled: true,
        voicemail_followup_delay_hours: 48
      });
    }
  };

  const saveFollowUpSettings = async () => {
    if (!showFollowUpSettings) return;
    
    try {
      const formData = new FormData();
      Object.entries(followUpSettings).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      await axios.put(`${API}/campaigns/${showFollowUpSettings.id}/followup-settings`, formData);
      toast.success("Follow-up settings saved!");
      setShowFollowUpSettings(null);
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to save follow-up settings");
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Campaigns
          </h1>
          <p className="text-gray-500 mt-1">Manage your AI calling campaigns</p>
          <TrustLine className="mt-2" />
        </div>
        <Button 
          data-testid="create-campaign-btn"
          onClick={() => setShowCreate(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Target className="w-16 h-16 text-gray-300 mx-auto" />
            <p className="text-gray-500 mt-4">No campaigns yet. Create your first campaign to start calling.</p>
            <Button 
              onClick={() => setShowCreate(true)}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map(campaign => (
            <Card key={campaign.id} className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`campaign-card-${campaign.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {campaign.name}
                    </CardTitle>
                    <StatusBadge status={campaign.status} />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`followup-settings-${campaign.id}`}
                      onClick={() => openFollowUpSettings(campaign)}
                      className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                      title="Follow-up Settings"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`edit-campaign-${campaign.id}`}
                      onClick={() => openEditCampaign(campaign)}
                      className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                      title="Edit Campaign"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`delete-campaign-${campaign.id}`}
                      onClick={() => deleteCampaign(campaign.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {campaign.description || "No description"}
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Total Calls</p>
                    <p className="text-lg font-semibold text-gray-900">{campaign.total_calls}</p>
                  </div>
                  <div className="text-center p-2 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-gray-500">Qualified</p>
                    <p className="text-lg font-semibold text-emerald-600">{campaign.qualified_leads}</p>
                  </div>
                </div>

                {/* Dial Settings Summary */}
                <div className="flex flex-wrap gap-2 mb-4 text-xs text-gray-500">
                  <span className="bg-gray-100 px-2 py-1 rounded">{campaign.calls_per_day || 100}/day</span>
                  {campaign.calls_per_hour > 0 && <span className="bg-gray-100 px-2 py-1 rounded">{campaign.calls_per_hour}/hr</span>}
                  <span className="bg-gray-100 px-2 py-1 rounded">{campaign.calling_hours_start || "09:00"}-{campaign.calling_hours_end || "17:00"}</span>
                </div>

                <Button
                  className="w-full"
                  data-testid={`toggle-campaign-${campaign.id}`}
                  variant={campaign.status === 'active' ? 'outline' : 'default'}
                  onClick={() => toggleCampaign(campaign)}
                >
                  {campaign.status === 'active' ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Pause Campaign
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start Campaign
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Create New Campaign
            </DialogTitle>
            <DialogDescription>
              Set up a new AI calling campaign with a custom script
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="campaign-name">Campaign Name *</Label>
              <Input
                id="campaign-name"
                data-testid="campaign-name-input"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="e.g., Q1 Credit Card Processing Outreach"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="campaign-desc">Description</Label>
              <Input
                id="campaign-desc"
                data-testid="campaign-desc-input"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                placeholder="Brief description of the campaign"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="ai-script">AI Script {newCampaign.ab_testing_enabled ? "(Variant A)" : "*"}</Label>
              <Textarea
                id="ai-script"
                data-testid="campaign-script-input"
                value={newCampaign.ai_script}
                onChange={(e) => setNewCampaign({...newCampaign, ai_script: e.target.value})}
                placeholder="Enter the script the AI will use during calls..."
                className="mt-1 min-h-[150px]"
              />
              <p className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded">
                💡 Use {'{company}'} for your company name, {'{contact_name}'} for the lead's name. Keep responses SHORT (1-2 sentences) for natural conversation flow.
              </p>
            </div>

            {/* DISC Personality Detection Questions */}
            <div className="border border-purple-200 rounded-lg p-4 bg-gradient-to-r from-purple-50 to-pink-50">
              <h4 className="font-medium text-purple-900 flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5" />
                🧠 DISC Personality Detection Questions
              </h4>
              <p className="text-sm text-purple-700 mb-3">Click to add to your script. AI will detect personality and adapt automatically.</p>
              
              <div className="space-y-3">
                {/* High Signal Question */}
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-purple-800">🎯 Highest Signal Question</span>
                    <Button 
                      type="button"
                      size="sm" 
                      variant="outline"
                      className="text-xs h-7 border-purple-300 text-purple-700 hover:bg-purple-100"
                      onClick={() => setNewCampaign({
                        ...newCampaign, 
                        ai_script: newCampaign.ai_script + "\n\nJust so I can tailor this for you—what's most important here: getting straight to results, or understanding all the details first?"
                      })}
                    >
                      + Add to Script
                    </Button>
                  </div>
                  <p className="text-sm text-gray-700 italic">
                    "Just so I can tailor this for you—what's most important here: getting straight to results, or understanding all the details first?"
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-500 text-white rounded text-[10px] flex items-center justify-center font-bold">D</span> "results"</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-blue-500 text-white rounded text-[10px] flex items-center justify-center font-bold">C</span> "details"</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-500 text-white rounded text-[10px] flex items-center justify-center font-bold">I</span> vague</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-500 text-white rounded text-[10px] flex items-center justify-center font-bold">S</span> "both"</span>
                  </div>
                </div>
                
                {/* Evaluation Question */}
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-purple-800">🔍 Evaluation Question</span>
                    <Button 
                      type="button"
                      size="sm" 
                      variant="outline"
                      className="text-xs h-7 border-purple-300 text-purple-700 hover:bg-purple-100"
                      onClick={() => setNewCampaign({
                        ...newCampaign, 
                        ai_script: newCampaign.ai_script + "\n\nWhen you're evaluating something like this, what matters more—ROI, ease of use, or team fit?"
                      })}
                    >
                      + Add to Script
                    </Button>
                  </div>
                  <p className="text-sm text-gray-700 italic">
                    "When you're evaluating something like this, what matters more—ROI, ease of use, or team fit?"
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-500 text-white rounded text-[10px] flex items-center justify-center font-bold">D</span> ROI</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-blue-500 text-white rounded text-[10px] flex items-center justify-center font-bold">C</span> specifics</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-500 text-white rounded text-[10px] flex items-center justify-center font-bold">I</span> team</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-500 text-white rounded text-[10px] flex items-center justify-center font-bold">S</span> ease</span>
                  </div>
                </div>
                
                {/* Both Questions */}
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
                  onClick={() => setNewCampaign({
                    ...newCampaign, 
                    ai_script: newCampaign.ai_script + "\n\nJust so I can tailor this for you—what's most important here: getting straight to results, or understanding all the details first?\n\n[After their response, use the follow-up:]\n\nWhen you're evaluating something like this, what matters more—ROI, ease of use, or team fit?"
                  })}
                >
                  + Add Both Questions to Script
                </Button>
              </div>
              
              <p className="text-xs text-purple-600 mt-3 italic">
                💡 AI automatically detects personality from responses and adapts its communication style in real-time.
              </p>
            </div>

            {/* A/B Testing Section */}
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/50">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-purple-900 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    A/B Script Testing
                  </h4>
                  <p className="text-sm text-purple-700">Test two scripts to see which converts better</p>
                </div>
                <Button
                  variant={newCampaign.ab_testing_enabled ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNewCampaign({...newCampaign, ab_testing_enabled: !newCampaign.ab_testing_enabled})}
                  className={newCampaign.ab_testing_enabled ? "bg-purple-600 hover:bg-purple-700" : ""}
                >
                  {newCampaign.ab_testing_enabled ? "Enabled" : "Enable"}
                </Button>
              </div>

              {newCampaign.ab_testing_enabled && (
                <div className="space-y-4 mt-4 pt-4 border-t border-purple-200">
                  <div>
                    <Label htmlFor="script-variant-b">Script Variant B</Label>
                    <Textarea
                      id="script-variant-b"
                      value={newCampaign.script_variant_b}
                      onChange={(e) => setNewCampaign({...newCampaign, script_variant_b: e.target.value})}
                      placeholder="Enter an alternative script to test against Variant A..."
                      className="mt-1 min-h-[120px] bg-white"
                    />
                  </div>

                  <div>
                    <Label>Traffic Split</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex-1">
                        <input
                          type="range"
                          min="10"
                          max="90"
                          step="10"
                          value={newCampaign.ab_split_percentage}
                          onChange={(e) => setNewCampaign({...newCampaign, ab_split_percentage: parseInt(e.target.value)})}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                          A: {newCampaign.ab_split_percentage}%
                        </span>
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-medium">
                          B: {100 - newCampaign.ab_split_percentage}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <p className="text-sm text-purple-800">
                      <strong>📊 How it works:</strong> Leads will be randomly assigned to Variant A or B. Track qualification rates in Analytics to see which script performs better.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Dial Settings */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Dial Settings
              </h4>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="calls-per-day">Calls Per Day</Label>
                    <Input
                      id="calls-per-day"
                      data-testid="campaign-calls-input"
                      type="number"
                      min="1"
                      value={newCampaign.calls_per_day}
                      onChange={(e) => setNewCampaign({...newCampaign, calls_per_day: parseInt(e.target.value) || 0})}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-400 mt-1">0 = unlimited</p>
                  </div>
                  <div>
                    <Label htmlFor="calls-per-hour">Calls Per Hour</Label>
                    <Input
                      id="calls-per-hour"
                      data-testid="campaign-calls-per-hour"
                      type="number"
                      min="0"
                      value={newCampaign.calls_per_hour}
                      onChange={(e) => setNewCampaign({...newCampaign, calls_per_hour: parseInt(e.target.value) || 0})}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-400 mt-1">0 = no limit (dials as fast as possible)</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="calling-start">Start Time</Label>
                    <Input
                      id="calling-start"
                      data-testid="campaign-start-time"
                      type="time"
                      value={newCampaign.calling_hours_start}
                      onChange={(e) => setNewCampaign({...newCampaign, calling_hours_start: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="calling-end">End Time</Label>
                    <Input
                      id="calling-end"
                      data-testid="campaign-end-time"
                      type="time"
                      value={newCampaign.calling_hours_end}
                      onChange={(e) => setNewCampaign({...newCampaign, calling_hours_end: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 -mt-2">TCPA enforces 8am-9pm in the lead's local time regardless of these settings</p>

                <div>
                  <Label>Calling Days</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[
                      { key: "mon", label: "Mon" },
                      { key: "tue", label: "Tue" },
                      { key: "wed", label: "Wed" },
                      { key: "thu", label: "Thu" },
                      { key: "fri", label: "Fri" },
                      { key: "sat", label: "Sat" },
                      { key: "sun", label: "Sun" }
                    ].map(day => (
                      <button
                        key={day.key}
                        type="button"
                        data-testid={`day-${day.key}`}
                        onClick={() => {
                          const days = newCampaign.calling_days || [];
                          const updated = days.includes(day.key)
                            ? days.filter(d => d !== day.key)
                            : [...days, day.key];
                          setNewCampaign({...newCampaign, calling_days: updated});
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                          (newCampaign.calling_days || []).includes(day.key)
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-600 border-gray-300 hover:border-blue-300"
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Example:</strong> 200 calls/day, 25 calls/hour, 9am-5pm Mon-Fri = steady pacing across 8 hours.
                    Set calls/hour to 0 for maximum speed.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="company-name">Your Company Name</Label>
              <Input
                id="company-name"
                value={newCampaign.company_name}
                onChange={(e) => setNewCampaign({...newCampaign, company_name: e.target.value})}
                placeholder="e.g., ABC Solutions"
                className="mt-1"
              />
              <p className="text-xs text-gray-400 mt-1">Used in AI greeting and voicemail</p>
            </div>

            {/* AI Conversation Settings */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">AI Conversation Settings</h4>
              
              <div className="mb-4">
                <Label htmlFor="response-wait">Response Wait Time (seconds)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Input
                    id="response-wait"
                    type="number"
                    min="1"
                    max="10"
                    value={newCampaign.response_wait_seconds}
                    onChange={(e) => setNewCampaign({...newCampaign, response_wait_seconds: parseInt(e.target.value) || 4})}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">seconds</span>
                  <div className="flex-1">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={newCampaign.response_wait_seconds}
                      onChange={(e) => setNewCampaign({...newCampaign, response_wait_seconds: parseInt(e.target.value)})}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  How long AI waits for caller to respond before continuing. Recommended: 4-6 seconds.
                </p>
              </div>
            </div>

            {/* Voicemail Drop Settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-base font-medium">Voicemail Drop</Label>
                  <p className="text-sm text-gray-500">Auto-leave voicemail when machine detected (saves ~$0.14/call)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newCampaign.voicemail_enabled}
                    onChange={(e) => setNewCampaign({...newCampaign, voicemail_enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                </label>
              </div>
              
              {newCampaign.voicemail_enabled && (
                <div>
                  <Label htmlFor="voicemail-message">Voicemail Drop Script</Label>
                  <Textarea
                    id="voicemail-message"
                    value={newCampaign.voicemail_message}
                    onChange={(e) => setNewCampaign({...newCampaign, voicemail_message: e.target.value})}
                    placeholder="Hi, this is {agent_name} with {company_name}. I just wanted to raise my hand and let you know I'm out here. I focus on helping businesses get more qualified leads. If you need help with more booked appointments or live transfers, I'm happy to have a discussion—even if it's just to bounce an idea. My number is 555-555-5555. Talk soon!"
                    className="mt-1 min-h-[100px]"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Variables: {'{contact_name}'}, {'{business_name}'}, {'{company_name}'}, {'{agent_name}'}
                  </p>
                  
                  {/* VM Drop Tips */}
                  <div className="mt-4 p-4 bg-violet-500/10 border border-violet-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-violet-400 text-lg">💡</span>
                      </div>
                      <div>
                        <p className="text-violet-300 font-medium text-sm mb-2">Pro Tip: Conversational beats salesy</p>
                        <p className="text-gray-400 text-xs mb-3">
                          Don't be pushy. Leave a friendly, low-pressure message that makes them <span className="text-white">want</span> to call back.
                        </p>
                        <div className="bg-black/20 rounded-lg p-3 text-xs text-gray-300 italic">
                          <p className="mb-2">"Hi, this is <span className="text-violet-300">[Your Name]</span> with <span className="text-violet-300">[Company]</span>. I just wanted to raise my hand and let you know I'm out here.</p>
                          <p className="mb-2">I focus on helping salespeople get more qualified leads. Anything you need in terms of more booked appointments or live transfers—I'm happy to have a discussion with you, even if it's just to bounce an idea.</p>
                          <p>I'm happy to help whether you're a client or not. My mobile is <span className="text-violet-300">[Your Number]</span>."</p>
                        </div>
                        <p className="text-gray-500 text-xs mt-2">✓ Friendly ✓ No pressure ✓ Offers value ✓ Easy callback</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ICP (Ideal Customer Profile) Settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-cyan-500" />
                <h4 className="font-medium">Ideal Customer Profile (ICP)</h4>
              </div>
              <p className="text-sm text-gray-500 mb-4">Configure which leads are prioritized for calling based on their fit.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Target Company Sizes</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["1-10", "11-50", "51-200", "201-500", "500+"].map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          const current = newCampaign.icp_config?.preferred_company_sizes || [];
                          const updated = current.includes(size) 
                            ? current.filter(s => s !== size)
                            : [...current, size];
                          setNewCampaign({
                            ...newCampaign, 
                            icp_config: {...newCampaign.icp_config, preferred_company_sizes: updated}
                          });
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          (newCampaign.icp_config?.preferred_company_sizes || []).includes(size)
                            ? "bg-cyan-100 border-cyan-500 text-cyan-700"
                            : "border-gray-300 text-gray-600 hover:border-gray-400"
                        }`}
                      >
                        {size} employees
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Preferred Roles</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["Owner", "CEO", "Manager", "Director", "VP", "CFO"].map(role => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => {
                          const current = newCampaign.icp_config?.preferred_roles || [];
                          const updated = current.includes(role) 
                            ? current.filter(r => r !== role)
                            : [...current, role];
                          setNewCampaign({
                            ...newCampaign, 
                            icp_config: {...newCampaign.icp_config, preferred_roles: updated}
                          });
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          (newCampaign.icp_config?.preferred_roles || []).includes(role)
                            ? "bg-cyan-100 border-cyan-500 text-cyan-700"
                            : "border-gray-300 text-gray-600 hover:border-gray-400"
                        }`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="target-industries">Target Industries (comma-separated)</Label>
                <Input
                  id="target-industries"
                  placeholder="e.g., Restaurant, Retail, Healthcare, Professional Services"
                  value={(newCampaign.icp_config?.target_industries || []).join(", ")}
                  onChange={(e) => {
                    const industries = e.target.value ? e.target.value.split(",").map(i => i.trim()).filter(i => i) : [];
                    setNewCampaign({
                      ...newCampaign,
                      icp_config: {...newCampaign.icp_config, target_industries: industries}
                    });
                  }}
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">Leave empty to target all industries</p>
              </div>

              <div className="mt-4">
                <Label htmlFor="min-icp">Minimum ICP Score to Dial</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Input
                    id="min-icp"
                    type="number"
                    min="0"
                    max="100"
                    value={newCampaign.min_icp_score || 0}
                    onChange={(e) => setNewCampaign({...newCampaign, min_icp_score: parseInt(e.target.value) || 0})}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">/ 100</span>
                  <div className="flex-1">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={newCampaign.min_icp_score || 0}
                      onChange={(e) => setNewCampaign({...newCampaign, min_icp_score: parseInt(e.target.value)})}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                  </div>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0 - Call all leads</span>
                  <span>40 - Skip D-tier</span>
                  <span>60 - Only A/B tier</span>
                  <span>80 - Only A-tier</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button 
              data-testid="save-campaign-btn"
              onClick={createCampaign}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-Up Settings Modal */}
      <Dialog open={!!showFollowUpSettings} onOpenChange={() => setShowFollowUpSettings(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <RefreshCw className="w-5 h-5 text-cyan-500" />
              Follow-Up Settings
            </DialogTitle>
            <DialogDescription>
              Configure automatic follow-up calls for "{showFollowUpSettings?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Auto Follow-Ups</Label>
                <p className="text-sm text-gray-500">Automatically schedule retry calls</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={followUpSettings.enabled}
                  onChange={(e) => setFollowUpSettings({...followUpSettings, enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>

            {followUpSettings.enabled && (
              <>
                {/* No-Answer Retry Settings */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <PhoneOff className="w-4 h-4 text-orange-500" />
                      <Label className="font-medium">No-Answer Retries</Label>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={followUpSettings.no_answer_retry_enabled}
                        onChange={(e) => setFollowUpSettings({...followUpSettings, no_answer_retry_enabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500"></div>
                    </label>
                  </div>

                  {followUpSettings.no_answer_retry_enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Max Retries</Label>
                        <Select
                          value={String(followUpSettings.no_answer_retry_count)}
                          onValueChange={(v) => setFollowUpSettings({...followUpSettings, no_answer_retry_count: parseInt(v)})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 retry</SelectItem>
                            <SelectItem value="2">2 retries</SelectItem>
                            <SelectItem value="3">3 retries</SelectItem>
                            <SelectItem value="5">5 retries</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Delay Between</Label>
                        <Select
                          value={String(followUpSettings.no_answer_retry_delay_hours)}
                          onValueChange={(v) => setFollowUpSettings({...followUpSettings, no_answer_retry_delay_hours: parseInt(v)})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 hours</SelectItem>
                            <SelectItem value="12">12 hours</SelectItem>
                            <SelectItem value="24">24 hours</SelectItem>
                            <SelectItem value="48">48 hours</SelectItem>
                            <SelectItem value="72">72 hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Voicemail Follow-Up Settings */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-500" />
                      <Label className="font-medium">Voicemail Follow-Up</Label>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={followUpSettings.voicemail_followup_enabled}
                        onChange={(e) => setFollowUpSettings({...followUpSettings, voicemail_followup_enabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>

                  {followUpSettings.voicemail_followup_enabled && (
                    <div>
                      <Label className="text-sm text-gray-600">Follow up after voicemail</Label>
                      <Select
                        value={String(followUpSettings.voicemail_followup_delay_hours)}
                        onValueChange={(v) => setFollowUpSettings({...followUpSettings, voicemail_followup_delay_hours: parseInt(v)})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24">24 hours</SelectItem>
                          <SelectItem value="48">48 hours (recommended)</SelectItem>
                          <SelectItem value="72">72 hours</SelectItem>
                          <SelectItem value="96">4 days</SelectItem>
                          <SelectItem value="168">1 week</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-2">
                        Give leads time to listen to voicemail before calling back
                      </p>
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <p className="text-sm text-cyan-800">
                    <strong>How it works:</strong> When a call ends with no-answer or voicemail, 
                    IntentBrain automatically schedules a follow-up call based on these settings. 
                    Follow-ups stop when the lead answers or gets booked.
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowUpSettings(null)}>Cancel</Button>
            <Button 
              onClick={saveFollowUpSettings}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="save-followup-settings-btn"
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o)=>{if(!o){setEditing(null);setEditDraft(null)}}}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Campaign</DialogTitle>
            <DialogDescription>Update schedule and voicemail settings. Saving does not start the campaign.</DialogDescription>
          </DialogHeader>
          {editDraft && <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Calls Per Day</Label><Input type="number" value={editDraft.calls_per_day} onChange={e=>setEditDraft({...editDraft,calls_per_day:+e.target.value})}/></div>
              <div><Label>Calls Per Hour</Label><Input type="number" value={editDraft.calls_per_hour} onChange={e=>setEditDraft({...editDraft,calls_per_hour:+e.target.value})}/></div>
              <div><Label>Start Time</Label><Input type="time" value={editDraft.calling_hours_start} onChange={e=>setEditDraft({...editDraft,calling_hours_start:e.target.value})}/></div>
              <div><Label>End Time</Label><Input type="time" value={editDraft.calling_hours_end} onChange={e=>setEditDraft({...editDraft,calling_hours_end:e.target.value})}/></div>
            </div>

            <div>
              <Label>Calling Days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[["mon","Mon"],["tue","Tue"],["wed","Wed"],["thu","Thu"],["fri","Fri"],["sat","Sat"],["sun","Sun"]].map(([k,l])=>
                  <button key={k} type="button" onClick={()=>{let x=editDraft.calling_days||[];setEditDraft({...editDraft,calling_days:x.includes(k)?x.filter(v=>v!==k):[...x,k]})}}
                    className={`px-3 py-1 rounded border ${editDraft.calling_days?.includes(k)?"bg-blue-600 text-white":"bg-white"}`}>{l}</button>
                )}
              </div>
            </div>

            <div><Label>Company Name</Label><Input value={editDraft.company_name} onChange={e=>setEditDraft({...editDraft,company_name:e.target.value})}/></div>
            <div><Label>Callback Number</Label><Input value={editDraft.callback_number} onChange={e=>setEditDraft({...editDraft,callback_number:e.target.value})}/></div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-blue-900">Campaign Leads</Label>
                  <p className="text-xs text-blue-700 mt-1">
                    Import a CSV directly into this campaign.
                  </p>
                </div>
                <Badge variant="secondary">
                  {campaignLeadCount === null ? "—" : `${campaignLeadCount} leads`}
                </Badge>
              </div>

              <input
                type="file"
                accept=".csv,text/csv"
                disabled={importingLeads}
                onChange={importCampaignCsv}
                className="block w-full text-sm border rounded-md p-2 bg-white cursor-pointer"
              />

              <p className="text-xs text-gray-600">
                CSV must include Business Name and Phone columns.
                Existing phone numbers will be updated instead of duplicated.
              </p>

              {importingLeads && (
                <p className="text-sm font-medium text-blue-700">
                  Importing leads...
                </p>
              )}
            </div>

            <div className="flex justify-between">
              <Label>Voicemail Enabled</Label>
              <input type="checkbox" checked={!!editDraft.voicemail_enabled} onChange={e=>setEditDraft({...editDraft,voicemail_enabled:e.target.checked})}/>
            </div>

            <div><Label>Voicemail Script</Label><Textarea className="min-h-[120px]" value={editDraft.voicemail_message} onChange={e=>setEditDraft({...editDraft,voicemail_message:e.target.value})}/></div>
            <div>
              <Label>Voice Agent</Label>
              <Select
                value={editDraft.agent_id || ""}
                onValueChange={(id) => {
                  const agent = agents.find(a => a.id === id);
                  setEditDraft({
                    ...editDraft,
                    agent_id: id,
                    callback_number: agent?.phone || editDraft.callback_number,
                    voicemail_audio_url: ""
                  });
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.filter(a => a.is_active !== false).map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.voice_type === "cloned" && a.cloned_voice_name ? ` — ${a.cloned_voice_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editDraft.voicemail_enabled && (
              <div className="rounded-lg border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Voicemail Audio</Label>
                  <Badge variant={editDraft.voicemail_audio_url ? "default" : "secondary"}>
                    {editDraft.voicemail_audio_url ? "Ready" : "Save to Generate"}
                  </Badge>
                </div>

                {editDraft.voicemail_audio_url ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const audio = new Audio(editDraft.voicemail_audio_url);
                      audio.play().catch(() => toast.error("Unable to play voicemail preview"));
                    }}
                  >
                    Preview Voicemail
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Save the campaign to generate the cloned voicemail audio before launch.
                  </p>
                )}
              </div>
            )}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>{setEditing(null);setEditDraft(null)}}>Cancel</Button>
            <Button onClick={saveEditCampaign}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
